import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faDog,
  faHandHoldingHeart,
  faUsers,
  faQuoteLeft,
} from "@fortawesome/free-solid-svg-icons";
import { showcaseDogs } from "../api/showcaseDogs";
import { capitalizeWords } from "../utils/capitalizeWords";
import { privateDogModel } from "../types/privateDogModel";
import profile from "../images/temp.svg";

const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const [featuredPups, setFeaturedPups] = useState<privateDogModel[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchFeaturedPups = async () => {
      try {
        const data = await showcaseDogs();
        // Response: [{ dogs: [{ dog: {...}, position: 1 }, ...] }]
        let showcasedItems: any[] = [];
        if (Array.isArray(data) && data.length > 0) {
          showcasedItems = Array.isArray(data[0]?.dogs) ? data[0].dogs : data;
        } else if (Array.isArray(data?.dogs)) {
          showcasedItems = data.dogs;
        }
        const dogs = showcasedItems
          .map((item: any) => item.dog ?? item)
          .filter(Boolean);
        setFeaturedPups(dogs.slice(0, 4));
      } catch (error) {
        setFeaturedPups([]);
      }
    };
    fetchFeaturedPups();
  }, []);

  const goTo = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const stats = [
    {
      value: "20",
      label: "rescue partners supported",
      valueClass: "text-blue-strong",
    },
    {
      value: "$15,000+",
      label: "donated to rescues that save at-risk dogs to support their medical care",
      valueClass: "text-orange",
    },
    {
      value: "200+",
      label: "dogs featured",
      valueClass: "text-blue-strong",
    },
  ];

  const involvementCards = [
    {
      icon: faHeart,
      title: "Adopt",
      description:
        "Browse adoptable dogs & start the adoption process with our shelter & rescue partners",
      buttonLabel: "Find a pup",
      link: "/browse",
    },
    {
      icon: faDog,
      title: "Foster",
      description:
        "Provide a safe temporary home & help a dog while they wait for adoption",
      buttonLabel: "Become a foster",
      link: "/foster-interest",
    },
    {
      icon: faHandHoldingHeart,
      title: "Donate",
      description:
        "Support rescue efforts, medical care & life-saving work for at-risk dogs",
      buttonLabel: "Donate now",
      link: "/donate",
    },
    {
      icon: faUsers,
      title: "Volunteer",
      description:
        "Are you a rescue, networker, or shelter volunteer? Become a Pup Profile Community Partner!",
      buttonLabel: "Join our team",
      link: "/get-involved",
    },
  ];

  const testimonials = [
    {
      quote:
        "Thank you so much for all that you do… and thank you for sharing her story and thank you to all of your donors",
      attribution: "Rescue & Pups Fund beneficiary",
    },
    {
      quote:
        "Fostering was an incredible experience. As a person who has never had a dog and wanted to dip our feet in the water, they provided us with all the education, materials, and flexible scheduling to make us feel right at home. We can’t wait to house the next dog!",
      attribution: "",
    },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col items-center gap-10 font-poppins">
      {/* Hero */}
      <section className="flex w-full flex-col items-center justify-between gap-10 px-6 py-10 lg:flex-row lg:gap-16 lg:px-16 lg:pb-0 lg:pt-12">
        {/* Copy */}
        <div className="flex w-full flex-col items-start justify-center lg:flex-1 lg:py-24">
          <div className="flex w-full max-w-[500px] flex-col gap-10">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-3">
                <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-gray-dark">
                  Woman-founded &amp; veteran-led
                </span>
                <h1 className="text-3xl font-extrabold leading-tight text-black sm:text-4xl">
                  Your one-stop platform to find, foster and support shelter
                  and rescue dogs in Southern California
                </h1>
              </div>
              <p className="text-lg font-normal text-gray-dark sm:text-xl">
                At Pup Profile, our mission is to showcase &amp; support dogs
                who are at risk of being euthanized at overcrowded shelters.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <button
                  onClick={() => goTo("/browse")}
                  className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
                >
                  Adopt
                </button>
                <button
                  onClick={() => goTo("/get-involved")}
                  className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
                >
                  Get Involved
                </button>
              </div>
              <button
                onClick={() => goTo("/login")}
                className="flex w-fit items-center gap-1 text-sm font-semibold text-blue-strong underline transition hover:text-blue-dark"
              >
                I&rsquo;m a rescue partner
                <svg
                  className="size-4"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M11.5 4.5v5a.75.75 0 0 1-1.5 0V6.31l-5.22 5.22a.75.75 0 0 1-1.06-1.06L8.94 5.25H5.75a.75.75 0 0 1 0-1.5h5a.75.75 0 0 1 .75.75Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="w-full lg:flex-1">
          <img
            src="/images/png-jpg/homepage/mainDog.jpg"
            alt="A rescue dog resting in the grass with a toy"
            className="aspect-square w-full rounded-xl object-cover"
          />
        </div>
      </section>

      {/* Stats */}
      <section className="flex w-full flex-col items-center justify-center gap-12 px-6 py-12 sm:flex-row sm:gap-[73px] lg:px-16">
        {stats.map((stat) => (
          <div
            key={stat.value}
            className="flex flex-1 flex-col items-center gap-2"
          >
            <p
              className={`text-5xl font-extrabold tracking-[0.96px] ${stat.valueClass}`}
            >
              {stat.value}
            </p>
            <p className="max-w-[234px] text-center text-base text-gray6">
              {stat.label}
            </p>
          </div>
        ))}
      </section>

      {/* Why we exist */}
      <section className="flex w-full flex-col items-stretch bg-blue-pale lg:flex-row">
        {/* Image */}
        <div className="w-full lg:w-[604px] lg:shrink-0">
          <img
            src="/images/png-jpg/homepage/missionDog.png"
            alt="A rescued pit bull running on the beach"
            className="aspect-square w-full object-cover"
          />
        </div>

        {/* Copy */}
        <div className="flex flex-1 flex-col items-start justify-center gap-10 px-6 py-12 lg:px-32 lg:py-16">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <span className="w-fit rounded bg-white px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                Why we exist
              </span>
              <h2 className="text-3xl font-bold text-blue-strong">
                Every dog deserves a chance
              </h2>
            </div>
            <p className="text-lg font-normal text-black sm:text-xl">
              Shelters across the country are facing overcrowding, leaving many
              adoptable dogs at risk. Pup Profile was created to help more dogs
              reach safety by connecting adopters, fosters, volunteers, and
              rescues in one place.
            </p>
          </div>
          <button
            onClick={() => goTo("/about")}
            className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
          >
            Our story
          </button>
        </div>
      </section>

      {/* Ways to get involved */}
      <section className="flex w-full flex-col items-center gap-12 px-6 py-16 lg:px-16 lg:py-32">
        <div className="flex flex-col items-center gap-3">
          <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
            Ways to get involved
          </span>
          <h2 className="text-center text-3xl font-bold text-blue-strong">
            Help more dogs find their forever home
          </h2>
        </div>

        <div className="flex flex-wrap items-stretch justify-center gap-10">
          {involvementCards.map((card) => (
            <div
              key={card.title}
              className="flex w-[271px] max-w-full flex-col items-center gap-8 p-6"
            >
              <div className="flex flex-1 flex-col items-center gap-6">
                <FontAwesomeIcon
                  icon={card.icon}
                  className="text-[2.5rem] text-gray1"
                />
                <div className="flex flex-col items-center gap-3">
                  <h3 className="text-2xl font-semibold text-black">
                    {card.title}
                  </h3>
                  <p className="text-center text-base font-normal text-gray-dark">
                    {card.description}
                  </p>
                </div>
              </div>
              <button
                onClick={() => goTo(card.link)}
                className="flex h-12 w-[223px] max-w-full items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
              >
                {card.buttonLabel}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Find your new best friend */}
      <section className="flex w-full flex-col items-center gap-12 bg-blue-pale px-6 pb-20 pt-16 lg:px-16">
        <div className="flex w-full flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <h2 className="text-3xl font-bold text-blue-strong">
            Find your new best friend
          </h2>
          <button
            onClick={() => goTo("/browse")}
            className="flex h-12 items-center justify-center gap-2 rounded-md text-base font-bold text-black transition hover:text-blue-strong"
          >
            Browse all dogs
            <svg
              className="size-6"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M11.5 4.5v5a.75.75 0 0 1-1.5 0V6.31l-5.22 5.22a.75.75 0 0 1-1.06-1.06L8.94 5.25H5.75a.75.75 0 0 1 0-1.5h5a.75.75 0 0 1 .75.75Z" />
            </svg>
          </button>
        </div>

        <div className="flex flex-wrap items-start justify-center gap-10">
          {featuredPups.map((pup) => (
            <button
              key={pup.dog_id}
              onClick={() => goTo(`/browse/${encodeURIComponent(pup.dog_id)}`)}
              className="flex w-[271px] max-w-full flex-col items-center overflow-hidden rounded-lg text-left transition hover:-translate-y-1"
            >
              <img
                src={pup.images?.[0]?.image || profile}
                alt={pup.images?.[0]?.alt ?? pup.name}
                className="h-[295px] w-full bg-gray-light object-cover"
              />
              <div className="flex w-full flex-col items-start gap-2 bg-blue-strong px-6 pb-6 pt-4 text-white">
                <p className="text-2xl font-semibold">{pup.name}</p>
                <div className="flex flex-col gap-1 text-base font-normal">
                  <p>{capitalizeWords(pup.sex)}</p>
                  <p>{capitalizeWords(pup.breed)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="flex w-full flex-col items-stretch justify-center gap-16 px-6 py-20 lg:flex-row lg:px-16">
        {testimonials.map((testimonial) => (
          <div
            key={testimonial.quote}
            className="flex w-full max-w-[624px] gap-8 bg-white p-6"
          >
            <FontAwesomeIcon
              icon={faQuoteLeft}
              className="shrink-0 text-[3.875rem] text-orange"
            />
            <div className="flex flex-col gap-3">
              <p className="text-xl font-medium text-black">
                {testimonial.quote}
              </p>
              {testimonial.attribution && (
                <p className="text-base font-normal text-gray-dark">
                  {testimonial.attribution}
                </p>
              )}
            </div>
          </div>
        ))}
      </section>

      {/* Donate to the Pups Fund */}
      <section className="flex w-full flex-col items-stretch bg-blue-pale lg:flex-row lg:justify-between">
        <div className="flex flex-col gap-10 px-6 py-12 lg:w-[650px] lg:py-20 lg:pl-32 lg:pr-20">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <span className="w-fit rounded bg-white px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                Donate to the pups fund
              </span>
              <h2 className="text-4xl font-extrabold text-black">
                Help fund a dog&rsquo;s second chance
              </h2>
            </div>
            <p className="text-xl font-normal text-gray-dark">
              100% of donations to{" "}
              <span className="font-semibold">The Pups Fund</span> go directly
              toward the care of rescue dogs. From routine medical care to
              emergency treatment, your support helps rescue partners save more
              dogs in need.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <button
              onClick={() => goTo("/donate")}
              className="flex h-12 min-w-[152px] items-center justify-center gap-2 rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
            >
              <FontAwesomeIcon icon={faHeart} className="text-lg" />
              Donate
            </button>
            <button
              onClick={() => goTo("/get-involved")}
              className="flex h-12 items-center justify-center gap-2 rounded-md text-base font-bold text-black transition hover:text-blue-strong"
            >
              More ways to help
              <svg
                className="size-6"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M11.5 4.5v5a.75.75 0 0 1-1.5 0V6.31l-5.22 5.22a.75.75 0 0 1-1.06-1.06L8.94 5.25H5.75a.75.75 0 0 1 0-1.5h5a.75.75 0 0 1 .75.75Z" />
              </svg>
            </button>
          </div>
        </div>
        <div className="lg:flex-1">
          <img
            src="/images/png-jpg/homepage/dog-fund.jpg"
            alt="A happy rescue dog supported by The Pups Fund"
            className="h-64 w-full object-cover sm:h-96 lg:h-full"
          />
        </div>
      </section>
    </div>
  );
};

export default Homepage;
